import CommonButton from "@/components/common/CommonButton";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Swal from "sweetalert2";
import ConfirmPopup from "@/components/common/ConfirmPopup";
import { fontFamily } from "@/styles/font";
import logger from "@/utils/logger";

// 약관 동의 상태 인터페이스 정의
interface AgreementState {
    all: boolean;
    service: boolean;
    privacy: boolean;
    marketing: boolean;
}

// 약관 링크 상수 추가
const AGREEMENT_LINKS = {
    service: "https://maket-b.notion.site/2b34493cf6904b43857d79bb1a77fa53?pvs=73",
    privacy: "https://maket-b.notion.site/28c577b66235405ab4a5b7b4432bd7ea?pvs=74",
    marketing: "https://maket-b.notion.site/522ecc9c54e44603bea1c2cec0aa0a39?pvs=73"
};

function Agreement() {
    const navigate = useNavigate();

    // 초기 상태를 세션 스토리지에서 불러오거나 기본값 사용
    const getSavedAgreements = (): AgreementState => {
        try {
            const savedAgreements = sessionStorage.getItem("agreements");

            if (savedAgreements) {
                const parsedAgreements = JSON.parse(savedAgreements) as AgreementState;

                // 모든 필드가 존재하는지 확인
                if (
                    "all" in parsedAgreements &&
                    "service" in parsedAgreements &&
                    "privacy" in parsedAgreements &&
                    "marketing" in parsedAgreements
                ) {
                    return parsedAgreements;
                }
            }
        } catch (error) {
            logger.error("약관 동의 데이터 파싱 오류:", error);
        }

        // 기본값 반환
        return {
            all: false,
            service: false,
            privacy: false,
            marketing: false
        };
    };

    const [agreements, setAgreements] = useState<AgreementState>(getSavedAgreements());
    const [showExitDialog, setShowExitDialog] = useState(false);

    // 약관 동의 상태가 변경될 때마다 세션 스토리지에 저장
    useEffect(() => {
        try {
            const agreementsString = JSON.stringify(agreements);
            sessionStorage.setItem("agreements", agreementsString);
        } catch (error) {
            logger.error("약관 동의 데이터 저장 오류:", error);
        }
    }, [agreements]);

    // 모든 약관 동의 처리
    const handleAllAgreement = () => {
        const newValue = !agreements.all;
        setAgreements({
            all: newValue,
            service: newValue,
            privacy: newValue,
            marketing: newValue
        });
    };

    // 개별 약관 동의 처리
    const handleAgreement = (key: keyof typeof agreements) => {
        const newAgreements = {
            ...agreements,
            [key]: !agreements[key]
        };

        // 모든 필수 약관이 체크되었는지 확인
        const allRequired = newAgreements.service && newAgreements.privacy;

        // 모든 약관이 체크되었는지 확인
        const allChecked = allRequired && newAgreements.marketing;

        newAgreements.all = allChecked;

        setAgreements(newAgreements);
    };

    // 회원가입 중단 및 데이터 정리 함수
    const clearJoinDataAndExit = () => {
        // 임시 토큰 삭제
        localStorage.removeItem("temporaryToken");

        // 세션 스토리지에서 회원가입 관련 데이터 삭제
        sessionStorage.removeItem("nickname");
        sessionStorage.removeItem("selectedGender");
        sessionStorage.removeItem("birthDate");
        sessionStorage.removeItem("selectedTypes");
        sessionStorage.removeItem("agreements");

        navigate(-1);
    };

    // 뒤로가기 처리
    const handleBack = () => {
        setShowExitDialog(true);
    };

    // 필수 약관 모두 동의 시 버튼 활성화 여부
    const isButtonEnabled = agreements.service && agreements.privacy;

    // 동의 버튼 클릭 핸들러 추가
    const handleAgreeClick = () => {
        if (isButtonEnabled) {
            try {
                // 약관 동의 데이터를 세션 스토리지에 명시적으로 저장
                const agreementsString = JSON.stringify(agreements);
                sessionStorage.setItem("agreements", agreementsString);

                // 다음 단계로 이동
                navigate("/join/nickname");
            } catch (error) {
                logger.error("약관 동의 데이터 저장 오류:", error);
            }
        }
    };

    // 약관 링크 열기 함수 추가
    const openAgreementLink = (type: keyof typeof AGREEMENT_LINKS, e: React.MouseEvent) => {
        e.stopPropagation(); // 체크박스 이벤트와 분리
        window.open(AGREEMENT_LINKS[type], "_blank");
    };

    // 브라우저 뒤로가기 버튼 처리
    useEffect(() => {
        // 브라우저 뒤로가기 이벤트 감지
        const handlePopState = (e: PopStateEvent) => {
            e.preventDefault();
            handleBack();
        };

        // 이벤트 리스너 등록
        window.addEventListener("popstate", handlePopState);

        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    return (
        <>
            <Container>
                {/* 네비게이션 바 */}
                <NavigationBar>
                    <TextActionFrame>
                        <IconButton onClick={handleBack}>
                            <BackIcon />
                        </IconButton>
                    </TextActionFrame>
                    <LargeTextContainer>
                        <Title>고객님의 동의가 필요해요</Title>
                        <SubTitle>서비스 이용을 위해 약관에 동의해주세요.</SubTitle>
                    </LargeTextContainer>
                </NavigationBar>

                {/* 약관 동의 섹션 */}
                <AgreementSection>
                    <AgreementContainer>
                        {/* 전체 동의 */}
                        <AllAgreementBox checked={agreements.all} onClick={handleAllAgreement}>
                            <CheckCircleIcon checked={agreements.all} />
                            <AgreementText weight="700">약관 모두 동의하기</AgreementText>
                        </AllAgreementBox>

                        {/* 개별 약관 목록 */}
                        <AgreementList>
                            {/* 서비스 이용약관 */}
                            <AgreementItem checked={agreements.service} onClick={() => handleAgreement("service")}>
                                <CheckIcon checked={agreements.service} />
                                <RequiredLabel>필수</RequiredLabel>
                                <AgreementItemText>서비스 이용약관</AgreementItemText>
                                <ChevronRightIcon
                                    onClick={(e) => openAgreementLink("service", e)}
                                    style={{ cursor: "pointer" }}
                                />
                            </AgreementItem>

                            {/* 개인정보 처리방침 */}
                            <AgreementItem checked={agreements.privacy} onClick={() => handleAgreement("privacy")}>
                                <CheckIcon checked={agreements.privacy} />
                                <RequiredLabel>필수</RequiredLabel>
                                <AgreementItemText>개인정보 처리방침</AgreementItemText>
                                <ChevronRightIcon
                                    onClick={(e) => openAgreementLink("privacy", e)}
                                    style={{ cursor: "pointer" }}
                                />
                            </AgreementItem>

                            {/* 마케팅 목적 개인정보 수집/이용 */}
                            <AgreementItem checked={agreements.marketing} onClick={() => handleAgreement("marketing")}>
                                <CheckIcon checked={agreements.marketing} />
                                <OptionalLabel>선택</OptionalLabel>
                                <AgreementItemText>마케팅 목적 개인정보 수집/이용</AgreementItemText>
                                <ChevronRightIcon
                                    onClick={(e) => openAgreementLink("marketing", e)}
                                    style={{ cursor: "pointer" }}
                                />
                            </AgreementItem>
                        </AgreementList>

                        {/* 동의 버튼 */}
                        <ButtonWrapper>
                            <CommonButton text="동의" enabled={isButtonEnabled} onClick={handleAgreeClick} />
                        </ButtonWrapper>
                    </AgreementContainer>
                </AgreementSection>
            </Container>

            <ConfirmPopup
                isOpen={showExitDialog}
                onClose={() => setShowExitDialog(false)}
                onConfirm={() => {
                    setShowExitDialog(false);
                    clearJoinDataAndExit();
                }}
                title="회원가입을 중단하시겠습니까? 지금 나가면 입력한 정보가 저장되지 않습니다."
            />
        </>
    );
}

// 스타일 컴포넌트
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    min-width: 320px;
    max-width: 430px;
    height: calc(100vh - 40px);
    margin: 0 auto;
    background: #ffffff;
    font-family: ${fontFamily.base};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
`;

const NavigationBar = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    height: 140px;
    background: #ffffff;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const TextActionFrame = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0px;
    gap: 262px;
    width: 100%;
    height: 48px;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
`;

const IconButton = styled.button`
    width: 48px;
    height: 48px;
    background: transparent;
    border: none;
    cursor: pointer;
    position: relative;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const BackIcon = styled.div`
    position: absolute;
    width: 24px;
    height: 24px;
    left: calc(50% - 24px / 2);
    top: calc(50% - 24px / 2);
    background-image: url("/icon/chevron-left.png");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
`;

const LargeTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    gap: 8px;
    width: 100%;
    height: 62px;
    background: #ffffff;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const Title = styled.h2`
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    color: #1f1f1f;
    margin: 0;
`;

const SubTitle = styled.p`
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 2.4vw, 16px);
    line-height: 1.5;
    color: #a9b5c0;
    margin: 0;
`;

const AgreementSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 16px;
    gap: 10px;
    width: 100%;
    height: auto;
    min-height: calc(100vh - 188px);
    border-radius: 0px;
    flex: none;
    order: 2;
    align-self: stretch;
    flex-grow: 0;
    box-sizing: border-box;
`;

const AgreementContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    gap: 24px;
    width: 100%;
    height: 100%;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const AllAgreementBox = styled.div<{ checked: boolean }>`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px;
    gap: 8px;
    width: 100%;
    height: 48px;
    background: #ffffff;
    border: 1px solid #e8eef2;
    border-radius: 8px;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
    cursor: pointer;
`;

const CheckCircleIcon = styled.div<{ checked: boolean }>`
    width: 20px;
    height: 20px;
    background: url(${(props) => (props.checked ? "/icon/able-check-filled.png" : "/icon/circle-check-filled.png")});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const AgreementList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    gap: 8px;
    width: 100%;
    height: auto;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const AgreementItem = styled.div<{ checked: boolean; optional?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px;
    gap: 8px;
    width: 100%;
    height: 36px;
    background: #ffffff;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
    cursor: pointer;
    position: relative;
`;

const CheckIcon = styled.div<{ checked: boolean; optional?: boolean }>`
    width: 20px;
    height: 20px;
    background: ${(props) => (props.checked ? "url(/icon/able-done.png)" : "none")};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const RequiredLabel = styled.span`
    width: 25px;
    height: 20px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: clamp(12px, 3.5vw, 14px);
    line-height: 20px;
    color: #17191a;
    flex: none;
    order: 1;
    flex-grow: 0;
`;

const OptionalLabel = styled.span`
    width: 25px;
    height: 20px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: clamp(12px, 3.5vw, 14px);
    line-height: 20px;
    color: #17191a;
    flex: none;
    order: 1;
    flex-grow: 0;
`;

const AgreementItemText = styled.span<{ optional?: boolean }>`
    width: auto;
    max-width: 256px;
    height: auto;
    min-height: 20px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 3.5vw, 14px);
    line-height: 20px;
    color: #17191a;
    flex: none;
    order: 2;
    flex-grow: 1;
`;

const ChevronRightIcon = styled.div`
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.5 15L12.5 10L7.5 5' stroke='%23464A4D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    flex: none;
    order: 3;
    flex-grow: 0;
`;

// ButtonWrapper 추가
const ButtonWrapper = styled.div`
    width: 100%;
    margin-top: auto;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const AgreementText = styled.span<{ weight?: string }>`
    width: auto;
    max-width: 309px;
    height: auto;
    min-height: 24px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: ${(props) => props.weight || "400"};
    font-size: clamp(14px, 4vw, 16px);
    line-height: 24px;
    color: #17191a;
    flex: none;
    order: 1;
    flex-grow: 1;
`;

export default Agreement;
